<template>
  <v-dialog :value="value" max-width="780" scrollable :fullscreen="$vuetify.breakpoint.xsOnly" @input="$emit('input', $event)">
    <v-card class="confirm" :class="`tone-${tone}`">
      <header class="confirm__head">
        <span class="confirm__icon"><v-icon large>{{ icon }}</v-icon></span>
        <div>
          <h3 class="text-h6 confirm__title">{{ title }}</h3>
          <p class="text-body-2 confirm__muted mb-0">{{ subtitle }}</p>
        </div>
      </header>

      <v-card-text class="confirm__body">
        <div v-if="summary.length" class="confirm__summary">
          <span v-for="(item, index) in summary" :key="index" class="confirm__chip">
            <v-icon small>{{ item.icon }}</v-icon>
            {{ item.text }}
          </span>
        </div>

        <table v-if="columns.length" class="confirm__table">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key" :class="{ 'is-number': column.numeric }">{{ column.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="index">
              <td v-for="column in columns" :key="column.key" :class="{ 'is-number': column.numeric }">
                <slot name="cell" :row="row" :column="column">{{ row[column.key] }}</slot>
              </td>
            </tr>
          </tbody>
        </table>

        <p v-if="total > rows.length" class="confirm__more">Se muestran los primeros {{ rows.length.toLocaleString() }} de {{ total.toLocaleString() }}.</p>

        <p v-if="notice" class="confirm__notice">{{ notice }}</p>
      </v-card-text>

      <v-card-actions class="confirm__actions">
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)">Cancelar</v-btn>
        <v-btn v-if="secondaryLabel" text @click="$emit('secondary')">{{ secondaryLabel }}</v-btn>
        <v-btn :color="confirmColor" class="white--text" @click="$emit('confirm')">{{ confirmLabel }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Diálogo de confirmación de la importación V2: encabezado, resumen, tabla de lo que se va a crear y aviso.
// Solo presenta: la lógica (qué filas, qué pasa al confirmar) vive en ManageProductImportV2.
export default {
  name: "ImportConfirmDialog",
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    icon: { type: String, default: "mdi-help-circle-outline" },
    tone: { type: String, default: "info", validator: (value) => ["info", "success", "warning"].includes(value) },
    confirmLabel: { type: String, default: "Aceptar" },
    secondaryLabel: { type: String, default: "" }, // acción alternativa opcional (emite "secondary")
    summary: { type: Array, default: () => [] }, // [{ icon, text }]
    columns: { type: Array, default: () => [] }, // [{ key, label, numeric? }]
    rows: { type: Array, default: () => [] },
    notice: { type: String, default: "" },
    total: { type: Number, default: 0 }, // filas en total, si rows es solo una muestra
  },
  computed: {
    confirmColor() {
      return { success: "green darken-3", warning: "deep-orange darken-3" }[this.tone] || "blue darken-2";
    },
  },
};
</script>

<style scoped>
.confirm {
  --tone-bg: #e3f0fa;
  --tone-ink: #1b4f72;
  display: flex;
  flex-direction: column;
}

.confirm.tone-success {
  --tone-bg: #e6f6ec;
  --tone-ink: #14532d;
}

.confirm.tone-warning {
  --tone-bg: #fff4e5;
  --tone-ink: #7e5109;
}

.confirm__head {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 24px 16px;
}

.confirm__icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: var(--tone-bg);
}

.confirm__icon .v-icon {
  color: var(--tone-ink) !important;
}

.confirm__title {
  margin: 0;
  color: rgba(0, 0, 0, 0.87);
}

.confirm__muted {
  color: rgba(0, 0, 0, 0.65);
}

.confirm__body {
  padding: 0 24px 16px !important;
}

.confirm__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.confirm__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background-color: var(--tone-bg);
  color: var(--tone-ink);
  font-size: 0.875rem;
  font-weight: 600;
}

.confirm__chip .v-icon {
  color: var(--tone-ink) !important;
}

.confirm__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.confirm__table th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.16);
  background-color: #fff;
  color: rgba(0, 0, 0, 0.65);
  font-size: 0.75rem;
  font-weight: 600;
  text-align: left;
}

.confirm__table td {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  color: rgba(0, 0, 0, 0.87);
  vertical-align: top;
}

.confirm__table .is-number {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.confirm__more {
  margin: 8px 0 0;
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.65);
}

.confirm__notice {
  margin: 16px 0 0;
  padding: 10px 14px;
  border-radius: 8px;
  background-color: #fff4e5;
  color: #7e5109;
  font-size: 0.875rem;
}

.confirm__actions {
  padding: 12px 24px 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
